import React, { useState } from 'react';
import { FileText, Upload, Download, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const Facturacion = () => {
  const [formData, setFormData] = useState({
    rfc: '',
    razonSocial: '',
    codigoPostal: '',
    regimenFiscal: '',
    usoCFDI: '',
    email: '',
    numeroOrden: '',
    monto: ''
  });
  const [comprobante, setComprobante] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setComprobante(file);
      toast({
        title: 'Archivo cargado',
        description: `${file.name} ha sido cargado correctamente.`,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulación de envío
    setTimeout(() => {
      toast({
        title: '¡Solicitud enviada!',
        description: 'Tu factura será enviada a tu correo en las próximas 24 horas.',
      });
      setIsSubmitting(false);
      // Reset form
      setFormData({
        rfc: '',
        razonSocial: '',
        codigoPostal: '',
        regimenFiscal: '',
        usoCFDI: '',
        email: '',
        numeroOrden: '',
        monto: ''
      });
      setComprobante(null);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Facturación</h1>
          <p className="text-xl opacity-90">Solicita tu factura de forma fácil y rápida</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Instrucciones */}
        <Card className="mb-8 shadow-lg">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-[#1e3a8a] flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Instrucciones para facturar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ol className="space-y-3 text-gray-600">
              <li className="flex items-start gap-3">
                <span className="bg-[#1e40af] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                <span>Realiza tu pago mediante transferencia bancaria en cualquiera de nuestras sucursales.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#1e40af] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                <span>Sube tu comprobante de pago en el formulario.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#1e40af] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                <span>Completa tus datos fiscales correctamente.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#1e40af] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">4</span>
                <span>Recibirás tu factura por correo electrónico en un plazo máximo de 24 horas.</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Formulario */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-[#1e3a8a]">Datos para facturación</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Datos de pago */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Información del pago</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de orden *
                    </label>
                    <Input
                      type="text"
                      name="numeroOrden"
                      placeholder="ej: ORD-12345"
                      value={formData.numeroOrden}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monto pagado *
                    </label>
                    <Input
                      type="number"
                      name="monto"
                      placeholder="ej: 1500.00"
                      value={formData.monto}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Comprobante de pago */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comprobante de pago *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1e40af] transition-colors cursor-pointer">
                  <input
                    type="file"
                    id="comprobante"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    required
                  />
                  <label htmlFor="comprobante" className="cursor-pointer">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      {comprobante ? comprobante.name : 'Haz clic para subir tu comprobante'}
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG o PDF (Máx. 5MB)</p>
                  </label>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Datos fiscales</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        RFC *
                      </label>
                      <Input
                        type="text"
                        name="rfc"
                        placeholder="XAXX010101000"
                        value={formData.rfc}
                        onChange={handleChange}
                        maxLength="13"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Código postal *
                      </label>
                      <Input
                        type="text"
                        name="codigoPostal"
                        placeholder="12345"
                        value={formData.codigoPostal}
                        onChange={handleChange}
                        maxLength="5"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Razón social *
                    </label>
                    <Input
                      type="text"
                      name="razonSocial"
                      placeholder="Nombre o Razón Social"
                      value={formData.razonSocial}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Régimen fiscal *
                      </label>
                      <select
                        name="regimenFiscal"
                        value={formData.regimenFiscal}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                        required
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="601">601 - General de Ley Personas Morales</option>
                        <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                        <option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                        <option value="606">606 - Arrendamiento</option>
                        <option value="608">608 - Demás ingresos</option>
                        <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                        <option value="621">621 - Incorporación Fiscal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Uso de CFDI *
                      </label>
                      <select
                        name="usoCFDI"
                        value={formData.usoCFDI}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                        required
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="G01">G01 - Adquisición de mercancías</option>
                        <option value="G03">G03 - Gastos en general</option>
                        <option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                        <option value="P01">P01 - Por definir</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correo electrónico *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tu factura será enviada a este correo
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm text-gray-600">
                <input type="checkbox" className="mt-1" required />
                <span>
                  Confirmo que los datos proporcionados son correctos y acepto que una vez generada 
                  la factura no podrá ser modificada.
                </span>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Procesando...'
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Solicitar factura
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Ayuda */}
        <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6">
          <h4 className="font-semibold text-yellow-800 mb-2">¿Necesitas ayuda?</h4>
          <p className="text-yellow-700 text-sm">
            Si tienes dudas sobre el proceso de facturación, contacta a nuestro equipo de soporte 
            al teléfono 5545547772 o envía un correo a info@morabiovet.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default Facturacion;